import { useEffect, useState } from "react";
import { CalendarClock, Mail, MailX, Settings2, Users } from "lucide-react";
import { reportsApi } from "../../api";
import { describeCron, formatDateTime, getApiErrorMessage } from "../../utils";
import { Button, EmptyState, LoadingText, SearchInput } from "../ui";
import type { MySubscription, UserReport } from "../../types/reports";

type SubscriptionsPanelProps = {
  active: boolean;
  myReports: UserReport[];
  onManageSchedule: (report: UserReport) => void;
};

function ownDelivery(report: UserReport): string {
  const s = report.schedule;
  if (!s) return "";
  const bits: string[] = [];
  if (s.deliver_email) bits.push("email");
  if (s.deliver_slack) bits.push(s.slack_channel_ids?.length ? "a Slack channel" : "Slack");
  return bits.join(" and ");
}

function matches(query: string, name: string, description?: string | null): boolean {
  if (!query) return true;
  return name.toLowerCase().includes(query) || (description ?? "").toLowerCase().includes(query);
}

export default function SubscriptionsPanel({ active, myReports, onManageSchedule }: SubscriptionsPanelProps) {
  const [subs, setSubs] = useState<MySubscription[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    if (!active || subs !== null) return;
    let cancelled = false;
    setLoading(true);
    reportsApi
      .listMySubscriptions()
      .then((res) => { if (!cancelled) setSubs(res.data); })
      .catch((err) => { if (!cancelled) setError(getApiErrorMessage(err, "Could not load your subscriptions")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, subs]);

  async function unsubscribe(sub: MySubscription) {
    if (!window.confirm(`Stop receiving "${sub.name}" emails?`)) return;
    setRemoving(sub.id);
    try {
      await reportsApi.unsubscribe(sub.id);
      setSubs((current) => (current ?? []).filter((s) => s.id !== sub.id));
    } catch { return; } finally {
      setRemoving(null);
    }
  }

  const query = search.trim().toLowerCase();
  const teamSubs = (subs ?? []).filter((s) => matches(query, s.name, s.description));
  const ownScheduled = myReports
    .filter((r) => r.schedule && (r.schedule.is_active ?? true) && (r.schedule.deliver_email || r.schedule.deliver_slack))
    .filter((r) => matches(query, r.name, r.description));

  const totalCount = (subs?.length ?? 0) + myReports.filter((r) => r.schedule && (r.schedule.is_active ?? true) && (r.schedule.deliver_email || r.schedule.deliver_slack)).length;

  if (loading && subs === null) {
    return <LoadingText className="py-12 text-center">Loading your subscriptions…</LoadingText>;
  }

  if (error && subs === null) {
    return <p className="py-12 text-center text-sm text-red-500">{error}</p>;
  }

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        Every scheduled report delivered to you, and why. Manage or turn any of them off here.
      </p>

      {totalCount > 0 && (
        <div className="mb-4 max-w-sm">
          <SearchInput
            id="subscriptions-search"
            label="Search your subscriptions"
            placeholder="Search subscriptions…"
            value={search}
            onChange={setSearch}
          />
        </div>
      )}

      {totalCount === 0 ? (
        <EmptyState
          bordered
          icon={<Mail size={28} aria-hidden="true" />}
          title="Nothing is scheduled to you"
          description="Schedule one of your own reports to email you, or an admin can add you to a team report."
        />
      ) : teamSubs.length === 0 && ownScheduled.length === 0 ? (
        <EmptyState
          bordered
          icon={<Mail size={28} aria-hidden="true" />}
          title="No matches"
          description="No subscriptions match your search."
        />
      ) : (
        <div className="space-y-6">
          {teamSubs.length > 0 && (
            <section>
              <div className="mb-2 flex items-center gap-2">
                <Users size={14} className="text-gray-400" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-gray-800">From your team</h3>
              </div>
              <p className="mb-2.5 text-xs text-gray-500">
                An admin set these up and added you as a recipient. Unsubscribe to stop the emails.
              </p>
              <ul className="space-y-2.5">
                {teamSubs.map((sub) => (
                  <li key={sub.id} className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-gray-800">{sub.name}</p>
                      {sub.description && (
                        <p className="mt-0.5 truncate text-xs text-gray-400" title={sub.description}>{sub.description}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Emailed to you · {describeCron(sub.cron_expression) ?? sub.cron_expression} · since {formatDateTime(sub.created_at)}
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => unsubscribe(sub)} disabled={removing === sub.id}>
                      <MailX size={13} aria-hidden="true" />
                      {removing === sub.id ? "Removing…" : "Unsubscribe"}
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {ownScheduled.length > 0 && (
            <section>
              <div className="mb-2 flex items-center gap-2">
                <CalendarClock size={14} className="text-gray-400" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-gray-800">Reports you scheduled</h3>
              </div>
              <p className="mb-2.5 text-xs text-gray-500">
                You set these to send on a schedule. Manage one to change the time or turn it off.
              </p>
              <ul className="space-y-2.5">
                {ownScheduled.map((report) => (
                  <li key={report.id} className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-gray-800">{report.name}</p>
                      {report.description && (
                        <p className="mt-0.5 truncate text-xs text-gray-400" title={report.description}>{report.description}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Sent to you by {ownDelivery(report)} · {report.schedule ? (describeCron(report.schedule.cron_expression) ?? report.schedule.cron_expression) : ""}
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => onManageSchedule(report)}>
                      <Settings2 size={13} aria-hidden="true" />
                      Manage
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
